import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@core/api/auth.api';
import { useAuthStore } from '@core/stores/auth.store';
import { API_BASE } from '@core/api/client';
import { emailFromToken, isTokenExpired } from '@core/auth/jwt';
import { FullPageSpinner } from '@ui/Spinner';
import {
  readSsoLanding,
  sameOriginPath,
  ssoLoginUrl,
  ssoNextUrl,
  type SsoLanding,
} from './ssoLogin';
import { notify } from '@core/notify/toast';
import { Button } from '@ui/Button';
import { IconButton } from '@ui/IconButton';
import { Icon } from '@ui/Icon';
import { Checkbox } from '@ui/Checkbox';
import { TextField } from '@ui/field/TextField';
import { loginSchema, type LoginFormValues } from './model/authSchemas';
import type { User } from '@core/types/auth';
import { useDocumentTitle } from '@core/hooks/useDocumentTitle';
import { isSsoOnly } from '@core/config/features';

const DEACTIVATED_ACCOUNT_MESSAGE = 'Your account is deactivated. Contact an administrator.';

/**
 * Split-screen login. Ports `features/auth/login/`.
 *
 * Three changes from the Angular version, all deliberate:
 *
 * 1. NAVIGATION LIVES HERE, not in the auth service. `AuthService.login()` called
 *    `router.navigateByUrl` and `MatSnackBar.open` from inside its `tap`, wrapped in
 *    `setTimeout(..., 500)` because the two raced. Sequencing them explicitly in the
 *    mutation's `onSuccess` removes the timeout entirely.
 *
 * 2. "REMEMBER ME" IS REAL. The Angular checkbox had no `formControlName`, so it was a
 *    decorative control that silently did nothing. Checked → localStorage (survives a
 *    browser restart); unchecked → sessionStorage (dies with the tab).
 *
 * 3. `returnUrl` is validated as a same-origin path (`sameOriginPath`) before
 *    navigating. `getReturnUrl()` read `window.location.search` and passed the value
 *    straight to `navigateByUrl` with no check — an open-redirect vector.
 *
 * `.login-title` renders at 17px, not the 28px in login.component.scss: styles.scss:1546
 * lists `.login-title` in the `!important` heading override.
 *
 * ============================================================================
 * THIS PAGE IS ALSO WHERE AZURE AD SSO LANDS.
 * ============================================================================
 *
 * Not by choice — the backend redirects to `{its own configured frontend}/login?token=…&next=…`
 * after the Microsoft round trip, whatever `next_url` we asked for. A dedicated callback route was
 * written first and then deleted: the backend never redirects to one, so it was dead code, and worse,
 * `next` pointing at it would have sent the browser there AFTER the token was already consumed, to
 * report a failure that had not happened. See `ssoLogin.ts` for the full round trip.
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const [hidePassword, setHidePassword] = useState(true);

  /**
   * The SSO outcome, when this is an SSO landing rather than an ordinary visit.
   *
   * ⚠️ THE URL IS READ ONCE, DURING THE FIRST RENDER, AND THAT ORDERING IS LOAD-BEARING. The effect
   * below STRIPS the token out of the address bar, so any later read would find a cleaned URL and
   * conclude that no token arrived — which is exactly what happens under React 19 StrictMode, where
   * effects run twice in development. A `useState` initialiser captures the URL before anything can
   * rewrite it, and `ssoHandled` keeps the side effects to a single pass.
   *
   * `useSearchParams` is NOT used for this. It is router state, and the `replaceState` below goes
   * behind the router's back deliberately; reading the token through a hook that re-renders on
   * navigation would reintroduce the same stale-read problem.
   *
   * ⚠️ AN EXPIRED TOKEN IS DEMOTED TO AN ERROR HERE, and that is a fix rather than a nicety. `setSession`
   * stores whatever it is given, but `isAuthenticated()` independently rejects an expired token — so the
   * success path would set the session, never flip to authenticated, and leave "Completing sign-in…" on
   * screen forever. Reachable without any bug on the backend's part: reload or revisit the landing URL
   * later, or hit enough clock skew. Collapsing it into the `error` shape reuses the path that already
   * toasts and shows the form.
   */
  const [ssoLanding] = useState<SsoLanding>(() => {
    const landing = readSsoLanding(
      window.location.search,
      window.location.hash,
      window.location.origin,
    );
    if (landing.kind === 'token' && isTokenExpired(landing.token)) {
      // Phrased to read correctly after the "Microsoft sign-in failed: " prefix below.
      return { kind: 'error', message: 'the token had already expired. Please sign in again.' };
    }
    return landing;
  });
  const ssoHandled = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  /**
   * `useWatch`, not `watch()`: the latter cannot be memoized, so the React Compiler skips this
   * component (`react-hooks/incompatible-library`). One subscription for the three fields.
   */
  const [email, password, rememberMe] = useWatch({
    control,
    name: ['email', 'password', 'rememberMe'],
  });

  useDocumentTitle('Login');

  const login = useMutation({
    mutationFn: (values: LoginFormValues) =>
      authApi.login({ email: values.email, password: values.password }),
    onSuccess: (response, values) => {
      // The backend has been observed to use either key (auth.service.ts:75).
      const token = response.token ?? response.access_token;
      if (!token) {
        notify.error('No token received from server');
        return;
      }

      const user: User = response.user ?? { email: values.email };
      setSession(token, user, values.rememberMe ? 'local' : 'session');
      notify.success('Login successful!');

      void navigate(sameOriginPath(searchParams.get('returnUrl')) ?? '/dashboard', {
        replace: true,
      });
    },
    // Errors are toasted by the axios interceptor using the server's own message.
  });

  /**
   * Establish the session the SSO redirect just delivered.
   *
   * ⚠️ THE TOKEN IS STRIPPED FROM THE ADDRESS BAR FIRST, on every path through here including the
   * failure ones. A credential sitting in a URL is written into browser history, leaks through the
   * `Referer` of the next request, and is recorded by anything that proxied it. `replaceState` keeps
   * it out of history and out of the bar the user is looking at; it cannot undo logging that already
   * happened upstream, which is a backend concern worth raising separately.
   *
   * `session` persistence, not `local`, and NOT the "Remember Me" checkbox. SSO is the identity
   * provider's session to own, so a long-lived copy in `localStorage` would outlive the sign-in the
   * user actually performed. Password login offers "remember me" because there this app IS the
   * authority.
   *
   * NO NAVIGATION HERE. `setSession` flips `isAuthenticated`, and the declarative redirect below
   * carries the browser to `returnTo` on the next render — one code path for both login methods,
   * instead of an imperative `navigate` racing a `<Navigate>`.
   */
  useEffect(() => {
    if (ssoHandled.current || ssoLanding.kind === 'none') return;
    ssoHandled.current = true;

    window.history.replaceState({}, '', window.location.pathname);

    if (ssoLanding.kind === 'error') {
      notify.error(
        ssoLanding.message === DEACTIVATED_ACCOUNT_MESSAGE
          ? ssoLanding.message
          : `Microsoft sign-in failed: ${ssoLanding.message}`,
      );
      return;
    }

    /*
     * ⚠️ THE EMAIL COMES OUT OF THE TOKEN, because the redirect carries nothing else. It is this
     * app's only identity anchor — see `emailFromToken`. Empty when the token holds no email-shaped
     * claim: the session is still valid and usable, and `greetingName` drops the greeting rather than
     * inventing one.
     */
    setSession(ssoLanding.token, { email: emailFromToken(ssoLanding.token) ?? '' }, 'session');
    notify.success('Signed in with Microsoft.');
  }, [ssoLanding, setSession]);

  /**
   * Hand the browser to the backend, which hands it to Microsoft.
   *
   * `next_url` is OUR OWN ORIGIN plus `returnUrl` — the page that sent the user here, such as an
   * emailed export link — and `ssoNextUrl` takes that only as a same-origin path. It is a redirect
   * target given to a server that will send a browser there carrying a fresh session, the same
   * open-redirect surface as note 3 above.
   *
   * `window.location.assign`, not `navigate()`: the destination is outside this SPA, so the router
   * cannot take us there.
   */
  const startSso = (): void => {
    window.location.assign(
      ssoLoginUrl(API_BASE, ssoNextUrl(window.location.origin, searchParams.get('returnUrl'))),
    );
  };

  // Angular did this in ngOnInit; a declarative redirect avoids the render-then-navigate flash.
  if (isAuthenticated) {
    /*
     * `returnTo` for an SSO landing, `/dashboard` otherwise. Both are same-origin paths already:
     * `safeNextPath` resolved `next` against our origin, and an already-signed-in visitor to /login
     * has no destination of their own.
     */
    return (
      <Navigate to={ssoLanding.kind === 'token' ? ssoLanding.returnTo : '/dashboard'} replace />
    );
  }

  /*
   * A token arrived but the session is not established yet — the effect has not run, or it ran and
   * the token was already expired. Rendering the login form for that frame would flash "please sign
   * in" at somebody who just did.
   */
  if (ssoLanding.kind === 'token') {
    return <FullPageSpinner message="Completing sign-in…" />;
  }

  return (
    <div className="flex h-screen w-full overflow-hidden max-[968px]:flex-col">
      {/* Brand panel. The layered gradients + geometric pattern are ported from
          login.component.scss:8-44. */}
      <div
        className="relative flex flex-1 items-center justify-center max-[968px]:h-[200px] max-[968px]:flex-none"
        style={{
          background:
            'linear-gradient(135deg, var(--vm-accent) 0%, var(--vm-accent-hover) 50%, var(--vm-accent-hover) 100%)',
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%),' +
              'linear-gradient(-45deg, rgba(255,255,255,0.05) 25%, transparent 25%),' +
              'linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.05) 75%),' +
              'linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.05) 75%)',
            backgroundSize: '100px 100px',
            backgroundPosition: '0 0, 0 50px, 50px -50px, -50px 0px',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 50%),' +
              'radial-gradient(circle at 80% 70%, rgba(255,255,255,0.08) 0%, transparent 50%)',
          }}
        />

        <div className="relative z-[1] rounded-xl bg-white px-7 py-5 shadow-lg max-[600px]:mx-5 max-[600px]:px-5 max-[600px]:py-4">
          <img
            src="/assets/verdantis-logo.svg"
            alt="Verdantis"
            width={420}
            height={98}
            fetchPriority="high"
            className="block h-auto w-[420px] max-w-full"
          />
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center bg-[#f8fafc] p-10 max-[968px]:p-5">
        <div className="bg-surface shadow-pop w-full max-w-[420px] rounded-[12px] border border-[#e5e7eb] p-10 max-[968px]:max-w-full max-[968px]:p-[30px] max-[600px]:p-6">
          {/*
            SSO ONLY (`VITE_SSO_ONLY`, production): no email / password form, no Remember Me and no
            Forgot Password — Microsoft is the only way in. See `isSsoOnly`.

            Laid out as a sign-in page of its own rather than the password card with the form taken
            out: a lone button under "Login" in an otherwise empty card read as broken (reported).
            The button follows Microsoft's sign-in button: light, with the four-square logo.
          */}
          {isSsoOnly() ? (
            <div className="flex flex-col items-center text-center">
              <h2 className="text-fg m-0 mb-8 text-[22px] font-semibold">Welcome</h2>
              <Button
                type="button"
                variant="outline"
                full
                className="h-[50px] min-h-[50px] gap-3 text-[16px] font-semibold normal-case"
                onClick={startSso}
              >
                <MicrosoftLogo />
                Sign in with Microsoft
              </Button>
              <p className="text-fg-3 mt-6 mb-0 text-xs">
                Access is managed by your organisation. Trouble signing in? Contact your
                administrator.
              </p>
            </div>
          ) : (
            <>
              <h2 className="login-title mb-8 max-[600px]:mb-6">Login</h2>
              <form onSubmit={handleSubmit((values) => login.mutate(values))} noValidate>
                <TextField
                  label="Email"
                  type="email"
                  required
                  autoComplete="email"
                  hasValue={!!email}
                  error={errors.email?.message}
                  {...register('email')}
                />

                <TextField
                  label="Password"
                  type={hidePassword ? 'password' : 'text'}
                  required
                  autoComplete="current-password"
                  hasValue={!!password}
                  error={errors.password?.message}
                  suffix={
                    <IconButton
                      label={hidePassword ? 'Show password' : 'Hide password'}
                      tabIndex={-1}
                      onClick={() => setHidePassword((value) => !value)}
                    >
                      <Icon>{hidePassword ? 'visibility_off' : 'visibility'}</Icon>
                    </IconButton>
                  }
                  {...register('password')}
                />

                <div className="mt-2 mb-6 flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
                  <Checkbox
                    checked={rememberMe}
                    onCheckedChange={(checked) => setValue('rememberMe', checked === true)}
                    label="Remember Me"
                  />
                  <Link
                    to="/forgot-password"
                    className="text-accent hover:text-accent-hover text-sm font-medium no-underline transition-colors hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="filled"
                  full
                  disabled={!isValid}
                  loading={login.isPending}
                  className="h-[50px] min-h-[50px] text-[17px] tracking-[0.5px] normal-case"
                >
                  {login.isPending ? 'Signing In...' : 'Login'}
                </Button>

                {/*
              ============================================================================
              Azure AD SSO — a full-page NAVIGATION, not a fetch
              ============================================================================

              ⚠️ IT CANNOT BE A `fetch`. `GET /auth/sso/login` answers a 3xx to Microsoft's origin;
              `fetch` sees `type: 'opaqueredirect'` with the `Location` hidden by CORS, and following it
              throws. The browser has to walk the redirect chain itself so Microsoft can present its
              login. See `ssoLogin.ts` for the full round trip.

              A plain `<a>` would do the navigation too, but this is a button so it reads as an action
              rather than a link to somewhere the user might middle-click into a new tab — a new tab
              would authenticate in a context that then closes.

              `type="button"` matters: inside a `<form>`, the default `submit` would post the empty
              credentials form instead.
            */}
                <div className="mt-3 flex items-center gap-2">
                  <span className="bg-line h-px flex-1" />
                  <span className="text-2xs text-fg-3 tracking-wide uppercase">or</span>
                  <span className="bg-line h-px flex-1" />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  full
                  className="mt-3 h-[46px] min-h-[46px] gap-3 normal-case"
                  onClick={startSso}
                >
                  <MicrosoftLogo />
                  Sign in with Microsoft
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Microsoft's four-square logo, as its sign-in button guidelines show it. Decorative: the label names it. */
function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
